import { useEffect } from 'react';
import '../styles/theme-4a.css';

/**
 * useTheme4A
 *
 * Opt-in cosmetic hook introduced for Batch 6. Pages that are in scope
 * for the "4a dot-trail + wave" look call this hook at the top of their
 * component. It adds a `theme-4a` class to <body> while the page is
 * mounted and removes it on unmount, so the CSS rules in
 * /app/frontend/src/styles/theme-4a.css only apply where they are
 * explicitly requested. Out-of-scope pages do not call this hook.
 *
 * Options:
 *   barHeight    - 56 (shared Footer.js) or 64 (Dashboard's own bar).
 *                  Controls where the auto-injected fixed wave strip sits
 *                  so it stays flush against the bar.
 *   enabled      - allow a page to toggle the theme on a per-tab basis
 *                  (ContentLibraryPage uses this to disable 4a on the
 *                  Lessons / Self-Work / Programme tabs).
 *   fixedDecor   - default `true`. When `false`, the global decor skips
 *                  the fixed dot-trail and the fixed wave strip. Pages
 *                  that have no fixed header or no bottom menu bar
 *                  (LandingPage, LoginPage, ResetPasswordPage, SetPinPage
 *                  and AuthCallback) pass `fixedDecor: false` and render
 *                  their own inline dot-trail / static wave if they want
 *                  them.
 */
export default function useTheme4A({ barHeight = 56, enabled = true, fixedDecor = true } = {}) {
  useEffect(() => {
    if (!enabled) return undefined;
    if (typeof document === 'undefined') return undefined;
    document.body.classList.add('theme-4a');
    if (barHeight === 64) {
      document.body.setAttribute('data-theme-4a-bar', '64');
    }
    if (!fixedDecor) {
      document.body.setAttribute('data-theme-4a-fixed-decor', 'off');
    }
    return () => {
      document.body.classList.remove('theme-4a');
      document.body.removeAttribute('data-theme-4a-bar');
      document.body.removeAttribute('data-theme-4a-fixed-decor');
    };
  }, [barHeight, enabled, fixedDecor]);
}
